/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosEnumMaterialCardBackground, GeminiCardFragment } from "@cosmos/hygraph-schema";

import {
	ButtonList,
	CosmosAsset,
	CosmosCard,
	CosmosRichText,
	CosmosText,
	hasContent,
	CosmosTextVariantEnum,
	EdsIcon,
	shiftHeadingTag,
	TagList,
	TextLinkGemini,
	TitleDescStack,
} from "@components/elements";
import type { CosmosCardProps } from "@components/elements";
import { useHeadingLevelOffset } from "@components/context";
import { css, cva, cx } from "@panda/css";
import { Stack, VStack } from "@panda/jsx";

const styles = css({
	height: "100%",
	maxW: "100%",
	gap: "eds.dimension.16",
	alignItems: "stretch",
	overflow: "hidden",
});

const ContentStyles = css({
	alignItems: "start",
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	flexGrow: "1",
	minW: "0",
	height: "100%",
	maxW: "100%",
	gap: "eds.dimension.32",
});

const ThumbnailStyles = css({
	maxWidth: "253px",
	height: "auto",
	alignSelf: "stretch",
	flexShrink: "0",
	mdOnly: {
		maxWidth: "100%",
		aspectRatio: "2/1",
	},
	smOnly: {
		maxWidth: "100%",
		aspectRatio: "2/1",
	},
	xsOnly: {
		flexShrink: "1",
	},
	xsDown: {
		maxWidth: "100%",
		aspectRatio: "2/1",
	},
});

const AssetStyles = css({
	height: "100%",
	width: "253px",
	alignSelf: "stretch",
	objectFit: "cover",
});

const linkStyles = cva({
	base: {
		width: "100%",
		display: "flex",
		flexDirection: "column",
		textAlign: "start",
	},
	variants: {
		hasEyebrow: {
			true: { gap: "eds.dimension.24" },
			false: { gap: "eds.dimension.16" },
		},
	},
});

export type GeminiCardProps = GeminiCardFragment & { animate?: boolean };

const backgroundToCardVariant = (backgorund: CosmosEnumMaterialCardBackground | null): CosmosCardProps["variant"] => {
	switch (backgorund) {
		case CosmosEnumMaterialCardBackground.Light:
			return "materialLight";
		case CosmosEnumMaterialCardBackground.Dark:
			return "materialDark";
		default:
			return "material";
	}
};

export function GeminiCard({
	eyebrow,
	heading,
	badge,
	icon,
	iconGradient,
	title,
	desc,
	links,
	buttons,
	modButtons,
	animate,
	asset,
	cardHref,
	background = CosmosEnumMaterialCardBackground.Default,
	anchorLinkId,
}: GeminiCardProps): React.JSX.Element {
	const buttonSource = modButtons?.length ? modButtons : buttons;

	const hasDesc = !!desc && hasContent(desc);
	const hasEyebrow = !!eyebrow && hasContent(eyebrow);
	const hasHeading = !!heading && hasContent(heading);
	const hasTitle = !!title?.html && hasContent(title.html);

	// Card title heading tag, resolved from the surrounding HeadingLevelContext: h3 by default,
	// shifted to h2 (floored) when the section title is promoted to the page h1 so the outline
	// never skips. Rendered as a wrapper element (not `as` on CosmosRichText) to keep the exact
	// DOM/visual of the design — offset 0 → <h3>, identical to before.
	const CardTitleTag = (shiftHeadingTag("h3", useHeadingLevelOffset()) ?? "h3") as "h2" | "h3" | "h4" | "h5" | "h6";

	return (
		<VStack
			id={anchorLinkId || undefined}
			alignItems="stretch"
			gap="eds.dimension.24"
			className={cx("gemini-card-molecule")}
			minWidth="0"
		>
			{hasHeading ? (
				// Card group-heading sits under the section h2, above the card title (h3): must be h3,
				// not h4 (which skipped h3). Visual size is variant-driven (headingXL), unchanged.
				<TitleDescStack title={{ ...heading, variant: "headingXL", as: "h3" }} className="gemini-card-heading" />
			) : null}
			<CosmosCard
				className={cx("gemini-card", styles)}
				variant={backgroundToCardVariant(background)}
				direction="geminiDir"
				padding={asset ? 16 : 24}
			>
				<CosmosCard.Content className={cx("gemini-card-content", ContentStyles)} padding={!asset ? "0!" : 8}>
					<Stack
						justify="start"
						rowGap="eds.dimension.24"
						columnGap="eds.dimension.16"
						height="100%"
						maxW="100%"
						minW="0"
						className={cx("gemini-card-content-top")}
					>
						<VStack className="gemini-link-list" gap="eds.dimension.16" alignItems="flex-start">
							{badge ? <TagList tags={[badge]}></TagList> : null}
							{icon ? <EdsIcon icon={icon} size="xl" gradient={iconGradient} /> : null}
							<CosmosCard.Link href={cardHref || ""} className={linkStyles({ hasEyebrow })}>
								{hasEyebrow ? <CosmosText variant="eyebrowMD" text={eyebrow} color="eds.text.secondary" /> : null}
								{hasTitle ? (
									<CardTitleTag>
										<CosmosRichText
											variant={CosmosTextVariantEnum.headingMD}
											color="eds.text.contrast"
											html={title.html}
											className={css({ overflowWrap: "anywhere" })}
										/>
									</CardTitleTag>
								) : null}
								{hasDesc ? (
									<CosmosRichText
										variant={CosmosTextVariantEnum.paragraphSM}
										color={hasEyebrow ? "eds.text.primary" : "eds.text.secondary"}
										html={desc}
										className={css({ overflowWrap: "anywhere" })}
									/>
								) : null}
							</CosmosCard.Link>
						</VStack>
						{links?.length ? (
							<VStack className="gemini-link-list" gap="eds.dimension.12" alignItems="flex-start">
								{links.map((link, i) => (
									<TextLinkGemini key={`gemini-link-${i}`} {...link} />
								))}
							</VStack>
						) : null}
					</Stack>
					<ButtonList animate={animate} buttons={buttonSource} size="lg" />
				</CosmosCard.Content>
				{asset ? (
					<CosmosCard.Thumbnail className={ThumbnailStyles} aspectRatio="unset">
						<CosmosAsset asset={asset} className={AssetStyles} />
					</CosmosCard.Thumbnail>
				) : null}
			</CosmosCard>
		</VStack>
	);
}
